import { TagsInput, type TagsInputProps } from "@mantine/core";
import classNames from "classnames";
import type { CommonFormFieldItemProps } from "../FormField";
import { FormFieldLabel } from "../FormFieldLabel";
import { FormFieldRightSection } from "../FormFieldRightSection";

interface FormFieldMultipleEmailsProps
  extends
    CommonFormFieldItemProps,
    Omit<
      TagsInputProps,
      "data" | "value" | "defaultValue" | "onChange" | "type"
    > {
  type: "multiple-emails";
  value?: Array<{ emailAddress: string }>;
  defaultValue?: Array<{ emailAddress: string }>;
  onChange?: (value: Array<{ emailAddress: string }>) => void;
}

/**
 * Multiple emails form field that handles array of email objects.
 * Converts between backend array format and TagsInput display format.
 */
function FormFieldMultipleEmails({
  helpText,
  highlight,
  isBetaFeature,
  type,
  warned,
  value,
  defaultValue,
  onChange,
  ...fieldSpecificProps
}: FormFieldMultipleEmailsProps) {
  const displayValues = value
    ? value.map((emailObj) => emailObj.emailAddress)
    : [];

  const handleChange = (newValues: Array<string>) => {
    const validEmails = newValues.filter((email) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    });
    const emailObjects = validEmails.map((email) => ({ emailAddress: email }));

    onChange?.(emailObjects);
  };

  return (
    <TagsInput
      {...fieldSpecificProps}
      aria-label={
        typeof fieldSpecificProps.label === "string"
          ? fieldSpecificProps.label
          : undefined
      }
      className={classNames(
        "FormFieldMultipleEmails",
        fieldSpecificProps.className
      )}
      data={[]}
      label={
        fieldSpecificProps.label && (
          <FormFieldLabel
            helpText={helpText}
            highlight={highlight}
            isBetaFeature={isBetaFeature}
            label={fieldSpecificProps.label}
            required={fieldSpecificProps.required}
          />
        )
      }
      labelProps={{ w: "100%", ...fieldSpecificProps.labelProps }}
      maxTags={5}
      placeholder="E-Mail-Adresse eingeben..."
      rightSection={
        <FormFieldRightSection
          rightSection={fieldSpecificProps.rightSection}
          warned={warned}
        />
      }
      splitChars={[",", " "]}
      type="email"
      value={displayValues}
      onChange={handleChange}
    />
  );
}

export { FormFieldMultipleEmails, FormFieldMultipleEmailsProps };
